import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  DirectionEnum,
  editFormDataAF,
  extractRideStationsDates,
  hookFormContextAF,
  IRideDto,
  IRideMetadata,
  isValidDateTime,
  Log,
  normalizeRideMetadata,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  rideEditHasMetadataChangedAtom,
  RideStationPanelMode,
  RideStatusEnum,
  RideUpdateModel,
  TRideDtoStartEndKey,
  TRideFieldName,
} from "@ryde/shared";

import { useRideEmptyStationsAllowed } from "./use-ride-empty-stations-allowed";
import { useRideStationsUpdateInfo } from "./use-ride-stations-update-info";

export const useRideDtoActions = () => {
  const { t } = useTranslation();
  const [rideDtoGlobal, setRideDtoGlobal] = useRecoilState<
    Partial<IRideDto> | undefined
    // should be hard-coded, because is used in drag context where PageContext is not initialized
    // also in ride live map page the page context is "rideMap", but for editing crud needs "ride"
  >(editFormDataAF(RideCrudModeKeyTuple));
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const { setMetadataUpdateInfo } = useRideStationsUpdateInfo();

  const rideEditHasMetadataChange = useSetRecoilState(
    rideEditHasMetadataChangedAtom,
  );

  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDtoGlobal,
  );

  const getEditingRideDtoId = useEvent((): number | undefined => {
    return rideDtoGlobal?.id;
  });

  const updateRideFormFieldsFromApiResponse = useEvent(
    (dtoUpdated: RideUpdateModel) => {
      if (!rideFormContext) {
        Log.error("Ride context not loaded");
        return;
      }

      const { setValue, getValues } = rideFormContext;
      const isAddNewRideForm = !dtoUpdated?.id;

      if (isAddNewRideForm) {
        if (dtoUpdated.siteId && !getValues<TRideFieldName>("siteId")) {
          Log.debug(
            "Ride site id is set from api response to",
            dtoUpdated.siteId,
          );
          setValue<TRideFieldName>("siteId", dtoUpdated.siteId, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    },
  );

  const updateEditingRideMetadataRideDate = useCallback(
    ({
      rideId,
      updatedMetadata,
      skipReorderStationsByTime,
    }: {
      rideId: number | undefined;
      updatedMetadata: IRideMetadata;
      skipReorderStationsByTime?: boolean;
    }): void => {
      if (
        updatedMetadata?.stationsInfo?.length &&
        updatedMetadata?.stationsInfo?.length > 1 &&
        updatedMetadata?.stationsInfo?.some(
          x => !isValidDateTime(x.arrivalTime),
        )
      ) {
        Log.warnAndToast(t("errors.stationsHaveNoArrivalTime"));
      }

      const metadataRecounted = normalizeRideMetadata(updatedMetadata, {
        direction: rideDtoGlobal?.direction || DirectionEnum.PickUp,
        rideSiteId: rideDtoGlobal?.siteId,
        skipReorderStationsByTime,
      });

      rideEditHasMetadataChange(true);
      setMetadataUpdateInfo({ ...rideDtoGlobal?.metadata }, metadataRecounted);
      setRideDtoGlobal(dto => {
        const { rideStart, rideEnd } = extractRideStationsDates(
          metadataRecounted.stationsInfo,
        );
        return {
          ...(dto ?? {}),
          ...(!isRideHasIndependentDayAdjustment && {
            rideStart: rideStart || dto?.rideStart,
            rideEnd: rideEnd || dto?.rideEnd,
          }),
          metadata: metadataRecounted,
        };
      });

      Log.debug(
        `Updating edited ride metadata with dates for ${rideId}`,
        metadataRecounted,
      );
    },
    [
      isRideHasIndependentDayAdjustment,
      rideDtoGlobal?.direction,
      rideDtoGlobal?.metadata,
      rideDtoGlobal?.siteId,
      rideEditHasMetadataChange,
      setMetadataUpdateInfo,
      setRideDtoGlobal,
      t,
    ],
  );

  const updateEditingRideMetadata = useEvent(
    (rideId: number | undefined, updatedMetadata: IRideMetadata): void => {
      if (
        updatedMetadata?.stationsInfo?.length &&
        updatedMetadata?.stationsInfo?.length > 1 &&
        updatedMetadata?.stationsInfo?.some(
          x => !isValidDateTime(x.arrivalTime),
        )
      ) {
        Log.warnAndToast(t("errors.stationsHaveNoArrivalTime"));
      }

      const metadataRecounted = normalizeRideMetadata(updatedMetadata, {
        direction: rideDtoGlobal?.direction || DirectionEnum.PickUp,
        isRideHasIndependentDayAdjustment,
        rideSiteId: rideDtoGlobal?.siteId,
      });

      rideEditHasMetadataChange(true);
      setMetadataUpdateInfo({ ...rideDtoGlobal?.metadata }, metadataRecounted);

      setRideDtoGlobal(dto => {
        return {
          ...(dto ?? {}),
          metadata: metadataRecounted,
        };
      });

      Log.debug(`Updating edited ride metadata ${rideId}`, metadataRecounted);
    },
  );

  const updateEditingRideDate = useEvent(
    (rideId: number, rideStart: TDateISO, rideEnd: TDateISO): void => {
      if (!isValidDateTime(rideStart) || !isValidDateTime(rideEnd)) {
        Log.warn("Invalid ride date supplied");
      }

      setRideDtoGlobal(dto => {
        const updatedRide: Partial<IRideDto> = {
          ...(dto || {}),
          rideStart,
          rideEnd,
        };
        return updatedRide;
      });
      Log.debug(`Updating edited ride date ${rideId}`, rideStart, rideEnd);
    },
  );

  const updateEditingRideSingleDate = useEvent(
    ({
      rideId,
      dateValue,
      fieldToUpdate,
      metadata,
      extraDates,
    }: {
      rideId: number | undefined;
      dateValue: TDateISO;
      fieldToUpdate: TRideDtoStartEndKey;
      metadata?: IRideMetadata;
      extraDates?: Partial<Pick<IRideDto, "rideStart" | "rideEnd">>;
    }): void => {
      if (!isValidDateTime(dateValue)) {
        Log.warn("Invalid ride date supplied");
      }

      if (!rideId) {
        Log.warn("Invalid ride id supplied");
      }

      setRideDtoGlobal(dto => {
        const updatedRide: Partial<IRideDto> = {
          ...(dto || {}),
          [fieldToUpdate]: dateValue,
          ...(extraDates || {}),
          ...(metadata ? { metadata } : {}),
        };
        return updatedRide;
      });
      Log.debug(`Updating edited ride date ${rideId}`, dateValue);
    },
  );

  const updateEditingRideDto = useEvent((dtoToUpdated: RideUpdateModel) => {
    const metadataRecounted = normalizeRideMetadata(
      dtoToUpdated?.metadata ?? {},
      {
        direction: dtoToUpdated?.direction || DirectionEnum.PickUp,
        rideSiteId: dtoToUpdated?.siteId,
      },
    );

    if (
      metadataRecounted?.stationsInfo?.length &&
      metadataRecounted?.stationsInfo?.length > 1 &&
      metadataRecounted?.stationsInfo?.some(
        x => !isValidDateTime(x.arrivalTime),
      )
    ) {
      Log.warnAndToast(t("errors.stationsHaveNoArrivalTime"));
    }

    rideEditHasMetadataChange(true);
    setMetadataUpdateInfo(
      { ...(rideDtoGlobal?.metadata || {}) },
      metadataRecounted,
    );

    setRideDtoGlobal({
      ...(dtoToUpdated ?? {}),
      metadata: metadataRecounted,
    });

    updateRideFormFieldsFromApiResponse(dtoToUpdated);
  });

  const updateEditingRideStatus = useEvent((rideStatus: RideStatusEnum) => {
    setRideDtoGlobal(dto => {
      const updatedRide: Partial<IRideDto> = {
        ...(dto || {}),
        status: rideStatus,
      };
      return updatedRide;
    });

    Log.debug(`Updating edited ride status ${rideStatus}`);
  });

  return {
    getEditingRideDtoId,
    updateEditingRideMetadata,
    updateEditingRideMetadataRideDate,
    updateEditingRideDate,
    updateEditingRideSingleDate,
    updateEditingRideDto,
    updateEditingRideStatus,
  };
};
